<script setup lang="ts">
import { computed, onMounted, ref } from 'vue';
import Button from 'primevue/button';
import Card from 'primevue/card';
import ChevronDownIcon from '@primeicons/vue/chevron-down';
import ChevronUpIcon from '@primeicons/vue/chevron-up';
import TrashIcon from '@primeicons/vue/trash';
import { useUtilities } from '@apparatix/composables';
import { Noir } from '@apparatix/theme/Noir';
import { useSelectionsStore } from '@apparatix/stores/useSelectionsStore';
import { savableActivityFilter, type SavedActivityFilter } from '@apparatix/schemas/Activities';
import { Stack, Toolbar, Typography } from '@apparatix/library/core';
import { createObjectFromSchema } from '@apparatix/schemas/Helpers';
import ActivityFilterEditor from './ActivityFilterEditor.vue';

type Props = {
  filters: SavedActivityFilter[],
  maxAllowedSections?: number,
};

type SectionModel = {
  filter: SavedActivityFilter,
}

type Emits = {
  (e: 'save', filters: SavedActivityFilter[], deletedKeys: string[]): void,
  (e: 'cancel'): void,
  (e: 'reset-filters'): void,
};

const props = withDefaults(defineProps<Props>(), {
  maxAllowedSections: 5,
});

const emit = defineEmits<Emits>();

const { deepClone } = useUtilities();
const selections = useSelectionsStore();

const sectionModel = ref<SectionModel[]>();
const deletedKeys = ref(new Set<string>());
const showResetWarning = ref(false);

const canAddMoreSections = computed(() => sectionModel.value?.length < props.maxAllowedSections);

const addNewSection = () => {
  if (!sectionModel.value) {
    sectionModel.value = [];
  }

  sectionModel.value.unshift({
    filter: createObjectFromSchema(savableActivityFilter),
  });

  updateSortOrders();
};

const deleteFilter = (filter: SavedActivityFilter) => {
  const idx = sectionModel.value.findIndex(section => section.filter.key === filter.key);
  if (idx === -1) {
    return;
  }

  sectionModel.value.splice(idx, 1);
  deletedKeys.value.add(filter.key);

  updateSortOrders();

  sectionModel.value.forEach(section => {
    const x = section.filter.excludeFromOtherSections?.findIndex(sectionKey => sectionKey === filter.key);
    if (x !== -1) {
      section.filter.excludeFromOtherSections.splice(x, 1);
    }
  });
};

const updateSortOrders = () => {
  for (let i=0; i<sectionModel.value.length; i++) {
    sectionModel.value[i].filter.sortOrder = i + 1;
  }
};

const reorderSection = (currentIndex: number, newIndex: number) => {
  const current = sectionModel.value[currentIndex];
  const target = sectionModel.value[newIndex];
  sectionModel.value[currentIndex] = target;
  sectionModel.value[newIndex] = current;

  updateSortOrders();
};

onMounted(() => {
  const clonedFilters = deepClone(props.filters);
  sectionModel.value = clonedFilters.sort((a, b) => a.sortOrder - b.sortOrder).map(filter => ({
    filter: createObjectFromSchema(savableActivityFilter, filter),
  }));
});
</script>
<template>
  <Stack>
    <template v-if="!showResetWarning">
      <Typography variant="body">
        You can customize each section displayed on your Activities home page, or create up to {{ props.maxAllowedSections }} of your own sections
      </Typography>
      <Toolbar align="left">
        <Button
          variant="outlined"
          :disabled="!canAddMoreSections"
          @click="addNewSection"
        >
          Add section
        </Button>
        <Button
          variant="outlined"
          severity="danger"
          @click="showResetWarning = true"
        >
          <TrashIcon />
          Reset filters
        </Button>
      </Toolbar>
    </template>
    <Stack v-if="showResetWarning" :gap="4">
      <Typography variant="body">
        <span class="text-[var(--p-red-500)]">Warning: </span>This will reset all of your filters back to default settings, and cannot be undone
      </Typography>
      <Toolbar align="left">
        <Button @click="showResetWarning = false">
          Cancel
        </Button>
        <Button
          variant="outlined"
          severity="danger"
          @click="emit('reset-filters')"
        >
          Reset filters
        </Button>
      </Toolbar>
    </Stack>
    <Card v-for="(section, idx) in sectionModel" :key="section.filter.key">
      <template #title>
        <div class="flex items-center justify-between">
          <Typography variant="section-subheading">
            {{ section.filter.filterName }}
          </Typography>
          <div class="flex flex-col gap-1">
            <Button
              icon-only
              aria-label="Move up"
              variant="text"
              size="small"
              :disabled="idx === 0"
              :dt="Noir.button"
              rounded
              @click="reorderSection(idx, idx - 1)"
            >
              <ChevronUpIcon />
            </Button>
            <Button
              icon-only
              aria-label="Move down"
              variant="text"
              size="small"
              :disabled="idx === sectionModel.length - 1"
              :dt="Noir.button"
              rounded
              @click="reorderSection(idx, idx + 1)"
            >
              <ChevronDownIcon />
            </Button>
          </div>
        </div>
      </template>
      <template #content>
        <Stack :gap="4">
          <ActivityFilterEditor
            v-model="section.filter"
            layout="column"
            mode="editor"
            :type-selections="selections.activities.types"
            :purpose-selections="selections.activities.purposes"
            :method-selections="selections.activities.methods"
            :user-selections="selections.salesScopedUsers"
            :other-section-selections="props.filters.filter(filter => filter.key !== section.filter.key && !deletedKeys.has(filter.key))"
          />
          <div>
            <Button
              size="small"
              severity="danger"
              variant="outlined"
              @click="deleteFilter(section.filter)"
            >
              <TrashIcon />
              Delete
            </Button>
          </div>
        </Stack>
      </template>
    </Card>
    <Toolbar>
      <Button
        severity="secondary"
        @click="emit('cancel')"
      >
        Cancel
      </Button>
      <Button @click="emit('save', sectionModel.map(section => ({...section.filter})), Array.from(deletedKeys))">
        Save
      </Button>
    </Toolbar>
  </Stack>
</template>
